import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import api from '../api/axiosConfig';
import './MeusCadastrosPage.css';
import './BuscaCertificadoPage.css';
import Footer from './Footer';

// Product warranty catalog (in months)
const PRODUCT_WARRANTY = {
  'Ecocoat Kenzo': 60,
  'Ecocoat Arcane': 48,
  'Ecocoat Elixir': 36,
  'Ecocoat Quartz': 24,
  'Ecocoat Ez': 12,
  'Ecocoat Wheel': 12,
  'Ecocoat Window': 12,
  'Ecocoat Leather': 12,
  'Ecocoat Trim': 24,
  'Ecocoat Premier': 6,
  'Premier': 6,
  'Ecocoat Eclipse': 12
};

function parseDate(dateStr) {
  if (!dateStr) return null;
  const str = String(dateStr).trim();
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(parseInt(iso[1]), parseInt(iso[2]) - 1, parseInt(iso[3]));
  const br = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (br) return new Date(parseInt(br[3]), parseInt(br[2]) - 1, parseInt(br[1]));
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

function getWarrantyMonths(productName) {
  const name = productName.trim().toLowerCase();
  for (const [key, months] of Object.entries(PRODUCT_WARRANTY)) {
    if (name === key.toLowerCase() || name.includes(key.toLowerCase()) || key.toLowerCase().includes(name)) {
      return months;
    }
  }
  return 12;
}

function formatWarrantyDuration(months) {
  if (months >= 12) {
    const years = months / 12;
    return years === 1 ? '1 ano' : `${years} anos`;
  }
  return `${months} meses`;
}
// Note: MeusCadastros uses its own formatWarrantyDuration; lang-aware version is in BuscaCertificado

function calcWarrantyInfo(productName, applicationDate) {
  const appDate = parseDate(applicationDate);
  if (!appDate) return { status: 'unknown', label: 'Data indisponivel' };
  const warrantyMonths = getWarrantyMonths(productName);
  const expiryDate = new Date(appDate);
  expiryDate.setMonth(expiryDate.getMonth() + warrantyMonths);
  const now = new Date();
  const diffMs = expiryDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  const totalDays = Math.ceil((expiryDate.getTime() - appDate.getTime()) / (1000 * 60 * 60 * 24));
  const percent = Math.max(0, Math.min(100, ((totalDays - Math.max(0, diffDays)) / totalDays) * 100));
  if (diffDays <= 0) {
    return { status: 'expired', label: 'Certificado expirado', expiryDate, daysLeft: 0, percent: 100, warrantyMonths };
  }
  const months = Math.floor(diffDays / 30);
  const days = diffDays % 30;
  let remaining = '';
  if (months > 0) remaining += `${months} ${months === 1 ? 'mes' : 'meses'}`;
  if (days > 0) remaining += `${remaining ? ' e ' : ''}${days} dias`;
  return { status: diffDays <= 90 ? 'warning' : 'active', label: remaining, expiryDate, daysLeft: diffDays, percent, warrantyMonths };
}

function calcMaintenanceSchedule(applicationDate) {
  const appDate = parseDate(applicationDate);
  if (!appDate) return { next: null, overdue: false, history: [] };
  const now = new Date();
  const schedule = [];
  let nextMaintenance = null;
  let overdue = false;
  for (let i = 1; i <= 10; i++) {
    const maintDate = new Date(appDate);
    maintDate.setMonth(maintDate.getMonth() + (i * 6));
    const isPast = maintDate < now;
    schedule.push({ date: maintDate, number: i, isPast });
    if (!nextMaintenance && maintDate >= now) {
      nextMaintenance = maintDate;
    }
  }
  const pastMaintenances = schedule.filter(s => s.isPast);
  if (pastMaintenances.length > 0 && nextMaintenance) {
    const daysUntilNext = Math.ceil((nextMaintenance.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysUntilNext <= 60) overdue = true;
  }
  return { next: nextMaintenance, overdue, history: schedule };
}

const parseProducts = (produtosStr) => {
  if (!produtosStr) return [];
  return produtosStr.split(',').map(p => p.trim()).filter(p => p.length > 0);
};

const MeusCadastrosPage = () => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [cadastros, setCadastros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aplicadorNome, setAplicadorNome] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCadastro, setSelectedCadastro] = useState(null);

  // Studios state
  const [estudios, setEstudios] = useState([]);
  const [estudiosLoading, setEstudiosLoading] = useState(false);
  const [showStudioForm, setShowStudioForm] = useState(false);
  const [editingStudio, setEditingStudio] = useState(null);
  const [studioForm, setStudioForm] = useState({
    nome: '', cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: ''
  });
  const [studioCepLoading, setStudioCepLoading] = useState(false);
  const [studioMessage, setStudioMessage] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const aplicador = JSON.parse(localStorage.getItem('user') || '{}');
    setAplicadorNome(aplicador.nome || '');

    fetchCadastros(token);
    fetchEstudios();
  }, [navigate]);

  const fetchEstudios = async () => {
    try {
      setEstudiosLoading(true);
      const resp = await api.get('/api/estudios');
      if (resp.data.success) setEstudios(resp.data.estudios);
    } catch (err) {
      console.error('Erro ao carregar estúdios:', err);
    } finally {
      setEstudiosLoading(false);
    }
  };

  const handleStudioFormChange = (e) => {
    setStudioForm({ ...studioForm, [e.target.name]: e.target.value });
  };

  const handleStudioCepChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    const formatted = digits.length <= 5 ? digits : `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
    setStudioForm(prev => ({ ...prev, cep: formatted }));
  };

  const buscarStudioCep = async () => {
    const digits = studioForm.cep.replace(/\D/g, '');
    if (digits.length !== 8) return;
    setStudioCepLoading(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await resp.json();
      if (!data.erro) {
        setStudioForm(prev => ({
          ...prev,
          endereco: data.logradouro || '', bairro: data.bairro || '',
          cidade: data.localidade || '', estado: data.uf || ''
        }));
      }
    } catch { /* ignore */ } finally { setStudioCepLoading(false); }
  };

  const handleStudioSubmit = async (e) => {
    e.preventDefault();
    setStudioMessage('');
    try {
      if (editingStudio) {
        await api.put(`/api/estudios/${editingStudio.id}`, studioForm);
      } else {
        await api.post('/api/estudios', studioForm);
      }
      setStudioMessage(t('studios.saveSuccess'));
      setShowStudioForm(false);
      setEditingStudio(null);
      setStudioForm({ nome: '', cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '' });
      fetchEstudios();
    } catch (err) {
      setStudioMessage(err.response?.data?.message || t('studios.errorSave'));
    }
  };

  const handleDeleteStudio = async (id) => {
    if (!window.confirm(t('studios.deleteConfirm'))) return;
    try {
      await api.delete(`/api/estudios/${id}`);
      setStudioMessage(t('studios.deleteSuccess'));
      fetchEstudios();
    } catch { setStudioMessage(t('studios.errorDelete')); }
  };

  const openEditStudio = (estudio) => {
    setEditingStudio(estudio);
    setStudioForm({
      nome: estudio.nome || '', cep: estudio.cep || '', endereco: estudio.endereco || '',
      numero: estudio.numero || '', complemento: estudio.complemento || '',
      bairro: estudio.bairro || '', cidade: estudio.cidade || '', estado: estudio.estado || ''
    });
    setShowStudioForm(true);
  };

  const fetchCadastros = async (token) => {
    try {
      setLoading(true);
      const response = await api.get('/api/meus-cadastros');

      if (response.data.success) {
        setCadastros(response.data.cadastros);
      }
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.removeItem('token');
        localStorage.removeItem('aplicador');
        navigate('/login');
        return;
      }
      setError('Erro ao carregar cadastros. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const filteredCadastros = cadastros.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (c.nomeCliente || '').toLowerCase().includes(term) ||
      (c.placa || '').toLowerCase().includes(term) ||
      (c.modeloCarro || '').toLowerCase().includes(term) ||
      (c.cpf || '').includes(term)
    );
  });

  const formatDateBR = (dateStr) => {
    if (!dateStr) return 'N/A';
    const str = String(dateStr).trim();
    const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
    if (str.includes('/')) return str;
    return str;
  };

  return (
    <div className="meus-cadastros-container">
      <div className="meus-cadastros-header">
        <button onClick={() => navigate('/cadastro')} className="back-btn">{t('common.backToCadastro')}</button>
        <Link to="/" className="logo-link">
          <div className="logo-wrapper-small">
            <div className="logo-circle-small">
              <img src="/logo.png" alt="IGL Coatings Logo" />
            </div>
          </div>
        </Link>
        <h1>{t('myCerts.title')}</h1>
        <p>{aplicadorNome ? t('myCerts.subtitle').replace('{{name}}', aplicadorNome) : t('myCerts.subtitleDefault')}</p>
      </div>

      {/* Studios Section */}
      <div className="studios-section">
        <div className="studios-header">
          <h2>{t('studios.title')}</h2>
          <button className="add-studio-btn" onClick={() => { setShowStudioForm(true); setEditingStudio(null); setStudioForm({ nome: '', cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '' }); }}>
            + {t('studios.addStudio')}
          </button>
        </div>
        {studioMessage && <div className="studio-message">{studioMessage}</div>}
        {estudiosLoading ? (
          <p style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}>{t('common.loading')}</p>
        ) : estudios.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', fontSize: '14px' }}>{t('studios.noStudios')}</p>
        ) : (
          <div className="studios-list">
            {estudios.map(est => (
              <div key={est.id} className="studio-card">
                <div className="studio-card-info">
                  <strong>{est.nome}</strong>
                  <span>{est.endereco}, {est.numero}{est.complemento ? ` - ${est.complemento}` : ''}</span>
                  <span>{est.bairro} - {est.cidade}/{est.estado}</span>
                </div>
                <div className="studio-card-actions">
                  <button onClick={() => openEditStudio(est)} className="studio-edit-btn">{t('studios.editStudio')}</button>
                  <button onClick={() => handleDeleteStudio(est.id)} className="studio-delete-btn">{t('studios.deleteStudio')}</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {showStudioForm && (
          <form className="studio-form" onSubmit={handleStudioSubmit}>
            <h4>{editingStudio ? t('studios.editStudio') : t('studios.addStudio')}</h4>
            <input name="nome" value={studioForm.nome} onChange={handleStudioFormChange} placeholder={t('register.studioNamePlaceholder')} required />
            <div className="cep-input-row">
              <input name="cep" value={studioForm.cep} onChange={handleStudioCepChange} placeholder={t('register.studioCepPlaceholder')} maxLength={9} required />
              <button type="button" className="cep-search-btn" onClick={buscarStudioCep} disabled={studioCepLoading || studioForm.cep.replace(/\D/g, '').length !== 8}>
                {studioCepLoading ? '...' : t('common.search')}
              </button>
            </div>
            <input name="endereco" value={studioForm.endereco} onChange={handleStudioFormChange} placeholder={t('register.studioEnderecoPlaceholder')} required />
            <div className="studio-form-row">
              <input name="numero" value={studioForm.numero} onChange={handleStudioFormChange} placeholder={t('register.studioNumeroPlaceholder')} required style={{ maxWidth: '100px' }} />
              <input name="complemento" value={studioForm.complemento} onChange={handleStudioFormChange} placeholder={t('register.studioComplementoPlaceholder')} />
            </div>
            <input name="bairro" value={studioForm.bairro} onChange={handleStudioFormChange} placeholder={t('register.studioBairroPlaceholder')} required />
            <div className="studio-form-row">
              <input name="cidade" value={studioForm.cidade} onChange={handleStudioFormChange} placeholder={t('register.studioCidadePlaceholder')} required />
              <input name="estado" value={studioForm.estado} onChange={handleStudioFormChange} placeholder={t('register.studioEstadoPlaceholder')} maxLength={2} required style={{ maxWidth: '70px' }} />
            </div>
            <div className="studio-form-actions">
              <button type="submit" className="studio-save-btn">{t('common.save')}</button>
              <button type="button" className="studio-cancel-btn" onClick={() => { setShowStudioForm(false); setEditingStudio(null); }}>{t('common.cancel')}</button>
            </div>
          </form>
        )}
      </div>

      <div className="meus-cadastros-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{t('myCerts.loadingCerts')}</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
            <button onClick={() => fetchCadastros(localStorage.getItem('token'))} className="retry-btn">
              {t('myCerts.retry')}
            </button>
          </div>
        ) : (
          <>
            <div className="cadastros-summary">
              <div className="summary-card">
                <span className="summary-number">{cadastros.length}</span>
                <span className="summary-label">{t('myCerts.totalRegistrations')}</span>
              </div>
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('myCerts.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>

            {filteredCadastros.length === 0 ? (
              <div className="empty-state">
                <p>{searchTerm ? t('myCerts.noResults') : t('myCerts.empty')}</p>
              </div>
            ) : (
              <div className="cadastros-grid">
                {filteredCadastros.map((cadastro, index) => (
                  <div key={index} className="cadastro-card cadastro-card-clickable" onClick={() => setSelectedCadastro(cadastro)}>
                    <div className="cadastro-card-header">
                      <div className="card-vehicle">
                        <span className="card-modelo">{cadastro.modeloCarro || 'Modelo N/A'}</span>
                        <span className="card-placa">{cadastro.placa || 'N/A'}</span>
                      </div>
                      <span className="card-date">{formatDateBR(cadastro.dataCadastro || cadastro.dataAplicacao)}</span>
                    </div>
                    <div className="cadastro-card-body">
                      <div className="card-info-row">
                        <span className="card-label">{t('myCerts.client')}</span>
                        <span className="card-value">{cadastro.nomeCliente || 'N/A'}</span>
                      </div>
                      <div className="card-info-row">
                        <span className="card-label">{t('myCerts.cpf')}</span>
                        <span className="card-value">{cadastro.cpf || 'N/A'}</span>
                      </div>
                      <div className="card-info-row">
                        <span className="card-label">{t('myCerts.color')}</span>
                        <span className="card-value">{cadastro.cor || 'N/A'}</span>
                      </div>
                      <div className="card-info-row">
                        <span className="card-label">{t('myCerts.products')}</span>
                        <span className="card-value card-products">{cadastro.produtosAplicados || 'N/A'}</span>
                      </div>
                      <div className="card-info-row">
                        <span className="card-label">{t('myCerts.serial')}</span>
                        <span className="card-value">{cadastro.serialNumber || 'N/A'}</span>
                      </div>
                      <div className="card-info-row">
                        <span className="card-label">{t('myCerts.local')}</span>
                        <span className="card-value">{cadastro.localAplicado || 'N/A'}</span>
                      </div>
                      {cadastro.certificadoId && (
                        <div className="card-info-row">
                          <span className="card-label">{t('myCerts.cert')}</span>
                          <span className="card-value card-cert-id">{cadastro.certificadoId}</span>
                        </div>
                      )}
                    </div>
                    {cadastro.fotoCarro && (
                      <div className="card-photo">
                        <img 
                          src={cadastro.fotoCarro} 
                          alt={`Veiculo ${cadastro.modeloCarro}`}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <div className="card-tap-hint">{t('myCerts.tapToView')}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de certificado completo */}
      {selectedCadastro && (() => {
        const certificado = selectedCadastro;
        const produtos = parseProducts(certificado.produtosAplicados);
        const maintenance = calcMaintenanceSchedule(certificado.dataAplicacao);

        return (
          <div className="certificado-modal-overlay" onClick={() => setSelectedCadastro(null)}>
            <div className="certificado-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close-btn" onClick={() => setSelectedCadastro(null)}>✕</button>
              
              <div className="certificate-card">
                <div className="certificate-header">
                  <div className="certificate-id">
                    <span className="id-label">ID do Certificado:</span>
                    <span className="id-value">{certificado.certificadoId}</span>
                  </div>
                  <div className="certificate-date">
                    <span className="date-label">Data de Aplicacao:</span>
                    <span className="date-value">{formatDateBR(certificado.dataAplicacao)}</span>
                  </div>
                </div>
                
                <div className="certificate-content">
                  <div className="vehicle-info">
                    <h3>Dados do Veiculo</h3>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Modelo:</span>
                        <span className="info-value">{certificado.modeloCarro}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Cor:</span>
                        <span className="info-value">{certificado.cor}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Placa:</span>
                        <span className="info-value">{certificado.placa}</span>
                      </div>
                      {certificado.serialNumber && (
                        <div className="info-item">
                          <span className="info-label">Serial Number:</span>
                          <span className="info-value">{certificado.serialNumber}</span>
                        </div>
                      )}
                      <div className="info-item">
                        <span className="info-label">Cliente:</span>
                        <span className="info-value">{certificado.nomeCliente || 'N/A'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">CPF:</span>
                        <span className="info-value">{certificado.cpf || 'N/A'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Aplicador:</span>
                        <span className="info-value">{certificado.aplicador || 'N/A'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Local:</span>
                        <span className="info-value">{certificado.localAplicado}</span>
                      </div>
                    </div>
                  </div>

                  {certificado.fotoCarro && (
                    <div className="vehicle-photo">
                      <h3>Foto do Veiculo</h3>
                      <div className="photo-display">
                        <img 
                          src={certificado.fotoCarro} 
                          alt={`Veiculo ${certificado.modeloCarro}`} 
                          className="certificate-photo"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="warranty-section">
                    <h3>Certificado por Produto</h3>
                    <div className="warranty-products-grid">
                      {produtos.map((produto, pIndex) => {
                        const warranty = calcWarrantyInfo(produto, certificado.dataAplicacao);
                        const warrantyLabel = formatWarrantyDuration(warranty.warrantyMonths || getWarrantyMonths(produto));
                        
                        return (
                          <div key={pIndex} className={`warranty-product-card warranty-${warranty.status}`}>
                            <div className="warranty-product-name">{produto}</div>
                            <div className="warranty-duration">Certificado: {warrantyLabel}</div>
                            
                            {warranty.expiryDate && (
                              <div className="warranty-expiry">
                                Valido ate: {warranty.expiryDate.toLocaleDateString('pt-BR')}
                              </div>
                            )}
                            
                            <div className="warranty-progress-bar">
                              <div 
                                className={`warranty-progress-fill progress-${warranty.status}`}
                                style={{ width: `${warranty.percent || 0}%` }}
                              ></div>
                            </div>
                            
                            <div className={`warranty-status-badge status-${warranty.status}`}>
                              {warranty.status === 'active' && <span>Ativa - Restam {warranty.label}</span>}
                              {warranty.status === 'warning' && <span>Atencao - Restam {warranty.label}</span>}
                              {warranty.status === 'expired' && <span>Certificado Expirado</span>}
                              {warranty.status === 'unknown' && <span>{warranty.label}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="maintenance-section">
                    <h3>Manutencao Recomendada</h3>
                    <div className="maintenance-info-text">
                      Recomendamos a manutencao do revestimento a cada <strong>6 meses</strong> a partir da data de aplicacao para manter a protecao e o brilho do veiculo.
                    </div>
                    
                    {maintenance.next && (
                      <div className={`maintenance-next-card ${maintenance.overdue ? 'maintenance-urgent' : 'maintenance-ok'}`}>
                        <div className="maintenance-next-icon">
                          {maintenance.overdue ? '!' : 'i'}
                        </div>
                        <div className="maintenance-next-content">
                          <div className="maintenance-next-title">
                            {maintenance.overdue ? 'Manutencao Proxima!' : 'Proxima Manutencao'}
                          </div>
                          <div className="maintenance-next-date">
                            {maintenance.next.toLocaleDateString('pt-BR')}
                          </div>
                          <div className="maintenance-next-days">
                            {(() => {
                              const days = Math.ceil((maintenance.next.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                              if (days <= 0) return 'Hoje ou em atraso';
                              if (days === 1) return 'Amanha';
                              return `Em ${days} dias`;
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                </div>
                
                <div className="certificate-footer">
                  <div className="warranty-info">
                    {(() => {
                      const allExpired = produtos.every(p => calcWarrantyInfo(p, certificado.dataAplicacao).status === 'expired');
                      const anyWarning = produtos.some(p => calcWarrantyInfo(p, certificado.dataAplicacao).status === 'warning');
                      if (allExpired) return <span className="warranty-badge badge-expired">CERTIFICADOS EXPIRADOS</span>;
                      if (anyWarning) return <span className="warranty-badge badge-warning">CERTIFICADO DE APLICACAO - ATENCAO</span>;
                      return <span className="warranty-badge badge-active">CERTIFICADO DE APLICACAO VALIDO</span>;
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <Footer />
    </div>
  );
};

export default MeusCadastrosPage;
